import { useMutation, useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo, useState } from "react";

import { DefaultVerifierDialog } from "./DefaultVerifierDialog";
import {
  DELETE_TENANT_DEFAULT_VERIFIER,
  type DefaultVerifierRow,
  TENANT_DEFAULT_VERIFIERS,
} from "./queries";
import { stateLabel } from "./stateLabel";

function fullName(person: DefaultVerifierRow["person"]) {
  return [person.first, person.last].filter(Boolean).join(" ");
}

/** What happens to the row's reports once it's removed. */
function removeMessage(removing: DefaultVerifierRow, rows: DefaultVerifierRow[]) {
  const prefix = `${fullName(removing.person)} will no longer be the required verifier for ${stateLabel(
    removing.state
  )}`;
  const tenantWide = rows.find((row) => row.state === null);
  if (removing.state !== null && tenantWide) {
    return `${prefix}. The "All other states" verifier, ${fullName(
      tenantWide.person
    )}, will apply to those reports instead.`;
  }
  return `${prefix}, so any verifier can be entered on those reports.`;
}

export function DefaultVerifiersTab({ tenantId }: { tenantId: string }) {
  const alerts = useAlerts();
  const { data, loading } = useQuery(TENANT_DEFAULT_VERIFIERS, {
    variables: { tenantId },
    fetchPolicy: "cache-and-network",
  });
  const [deleteVerifier, { loading: deleting }] = useMutation(DELETE_TENANT_DEFAULT_VERIFIER, {
    refetchQueries: [TENANT_DEFAULT_VERIFIERS],
  });
  // `existing: null` opens the dialog to add a new row.
  const [dialog, setDialog] = useState<{ existing: DefaultVerifierRow | null } | null>(null);
  const [removing, setRemoving] = useState<DefaultVerifierRow | null>(null);

  const rows = useMemo(() => data?.tenantDefaultVerifiers ?? [], [data]);

  const columns: CustomGridColDef<DefaultVerifierRow>[] = useMemo(
    () => [
      {
        field: "state",
        headerName: "State",
        flex: 1,
        valueGetter: (_value, row) => stateLabel(row.state),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        valueGetter: (_value, row) => fullName(row.person),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        valueGetter: (_value, row) => row.titleOverride ?? row.person.title,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        valueGetter: (_value, row) => row.person.email,
      },
      {
        field: "actions",
        type: "actions",
        getActions: ({ row }) => [
          <Tooltip title="Edit default verifier" key="edit">
            <GridActionsCellItem
              label="Edit default verifier"
              icon={<Edit />}
              onClick={() => setDialog({ existing: row })}
            />
          </Tooltip>,
          <Tooltip title="Remove default verifier" key="remove">
            <GridActionsCellItem
              label="Remove default verifier"
              icon={<Delete />}
              onClick={() => setRemoving(row)}
            />
          </Tooltip>,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Typography variant="body2" color="text.secondary">
          Pre-fills the verifier on this organization&apos;s Tier II reports. Nobody can change it
          on a report, including staff. A state&apos;s own row takes priority over &quot;All other
          states&quot;.
        </Typography>
        <Button
          variant="contained"
          onClick={() => setDialog({ existing: null })}
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          Add default verifier
        </Button>
      </Stack>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        hideFooter
        noRowsMessage="No default verifiers"
      />
      {dialog && (
        <DefaultVerifierDialog
          tenantId={tenantId}
          existing={dialog.existing}
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDialog
        open={!!removing}
        header="Remove default verifier?"
        info={removing ? removeMessage(removing, rows) : undefined}
        confirmText="Remove"
        loading={deleting}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await deleteVerifier({ variables: { id: removing.id } });
            alerts.success("Default verifier removed");
            setRemoving(null);
          } catch (err) {
            alerts.error("Error removing default verifier", err);
          }
        }}
      />
    </Stack>
  );
}
